import React, { useContext, useEffect, useMemo } from 'react'
import { avg } from 'simple-utils';
import { formatDate } from 'simple-utils/dist/date-utils';
import { ReactTooltip } from 'tooltip';
import { CounterCard, MoneyCounterCard } from '../components/CounterCard';
import { isIpad } from '../components/utils';
import { getAccountEventInfo, logEvent } from '../ga/ga-event-logger';
import { AuthContext } from '../login/AuthContext';
import { Account, getAccountLink, getAccountStatusLink, getAdsLink, getBillingLink, getDetailReportLink, getGoogleLink, getGooglePaymentLink, getLeadsProjection, getPageLink, getPageStatusLink, goalClass, isGoogle, monthGoal, suggestedBudget } from '../models/Account';
import { cpi, cpl, cpm, FullReportEntry, missingInfoDayCount, percentage } from '../models/FullReportEntry';
import { useFranchiseTags } from '../tags/tag-context';
import { TagAccountEditorButton, TagPicker } from '../tags/tag-editor';
import { AccountTags, TagDisplay } from '../tags/tags';
import { bigNumber, decimal, formatMoney } from '../utils/money-utils';
import { StatusRender, TrendRender, formatStrDate } from './AccountComponents';
import { AccountContext, useShowPrevious } from './AccountContext'
import { ClickChart } from './charts/ClickChart';
import { CostChart } from './charts/CostChart';
import { LeadChart } from './charts/LeadChart';
import { Observation } from './comments/Observation';
import { ToggleFilter } from './filtering/FilterModal';
import { PeriodLabel } from './PeriodSelector';

function Balance({account}:{account:FullReportEntry}){
    if(account.balance === null)
        return null;
    const min = account.dayBudget * 3;
    function getClass(){
        if(account.balance < min) return 'badge bg-danger';
        if(account.balance < min * 2) return 'badge bg-warning';
        return ''
    } 
    return <><b>Saldo Aproximado: </b> <span className={getClass()}>{formatMoney(account.balance)}</span><br /></>
}

function AccountLinks({account}:{account:FullReportEntry}){
    const ctx = useContext(AuthContext);
    const target = isIpad() ? "_self" : "_blank";

    if(isGoogle(account))
        return <div className='mt-4 mb-2'>
        <a className="btn btn-primary btn-sm mb-2 me-2" target={target} href={getGoogleLink(account)}> <i className="fas fa-external-link me-1"></i> Google Ads Manager </a>
        <a className="btn btn-primary btn-sm mb-2 me-2" target={target} href={getGooglePaymentLink(account)}> <i className="fas fa-external-link me-1"></i> Pagamento</a>    
    </div>

    return <div className='mt-4 mb-2'>
        <a className={`btn ${account.status != 0 ? 'btn-danger' : 'btn-primary'} btn-sm mb-2 me-2`} target={target} href={getBillingLink(account)}> <i className="fas fa-external-link me-1"></i> {account.status != 0  ? 'Corrigir' : 'Pagamento' } </a>    
        <a className="btn btn-primary btn-sm mb-2 me-2" target={target} href={getAccountLink(account)}> <i className="fas fa-external-link me-1"></i> Meta Ads Manager </a>
        { account.pageId && <a className="btn btn-secondary btn-sm mb-2 me-2" target={target} href={getAdsLink(account)}> <i className="fa fa-bullhorn me-1"></i> Biblioteca de anúncios</a>}
        { account.pageId && <a className="btn btn-secondary btn-sm mb-2 me-2" target={target} href={getPageLink(account)}> <i className="fab fa-facebook me-1"></i> Acessar Página</a>}
        { account.pageId && <a className="btn btn-secondary btn-sm mb-2 me-2" target={target} href={getAccountStatusLink(account)}> <i className="fab fa-meta me-1"></i> Status da Conta</a>}
        { account.pageId && <a className="btn btn-secondary btn-sm mb-2 me-2" target={target} href={getPageStatusLink(account)}> <i className="fab fa-facebook me-1"></i> Status da Página</a>}
        { account.info?.googleCustomerId && <a className="btn btn-primary btn-sm mb-2 me-2" target={target} href={getGoogleLink(account)}> <i className="fas fa-external-link me-1"></i> Google Ads Manager </a>}
        <a className="btn btn-secondary btn-sm mb-2 me-2" target={target} href={getDetailReportLink(account)}> <i className="far fa-file-lines me-1"></i> Relatório Individual</a>
    </div>
}

function BigNumberRender({current, prev}:{current:number, prev:number}){
    const show = useShowPrevious();
    return <>
        {bigNumber(current)}
        {show && <><br /><small className='text-muted'>{bigNumber(prev)}</small></>}
    </>
}

function PercentNumberRender({current, prev}:{current:number, prev:number}){
    const show = useShowPrevious();
    return <>
        {decimal((current || 0) * 100)}%
        {show && <><br /><small className='text-muted'>{decimal((prev || 0) * 100)}%</small></>}
    </>
}

function MoneyRender({current, prev}:{current:number, prev:number}){
    const show = useShowPrevious();
    return <>
        {formatMoney(current)}
        {show && <><br /><small className='text-muted'>{formatMoney(prev)}</small></>}
    </>
}

function DecimalRender({current, prev}:{current:number, prev:number}){
    const show = useShowPrevious();
    return <>
        {decimal(current)}
        {show && <><br /><small className='text-muted'>{decimal(prev)}</small></>}
    </>
}

function PercentRender({current, prev}:{current:number, prev:number}){
    const show = useShowPrevious();
    return <>
        {decimal(current * 100)}%
        {show && <><br /><small className='text-muted'>{decimal(prev * 100)}%</small></>}
    </>
}

function fixOffset(e:HTMLDivElement){
    const holder = e.offsetParent

    console.log(e.offsetTop > (holder.scrollTop + 50) ,  e.offsetTop < (holder.scrollTop + holder.clientHeight * .75 ))

    if(e.offsetTop > (holder.scrollTop + 50) && e.offsetTop < (holder.scrollTop + holder.clientHeight * .75 ))
        return;
    
    console.log("scrollCall", e.offsetTop - 120)

    holder.scrollTo({
        top: e.offsetTop - 120,
        left: 0,
        behavior: "smooth",
    });
}

function PastMonthLeads({account}:{account:FullReportEntry}){
    const diff = account.nLeads - account.pastMonthSameDay.leads
    const icon  = diff > 0 ? 'fa fa-arrow-up' : 'fa fa-arrow-down';
    const ratio = account.nLeads ? diff / account.nLeads : 100

    return <div className='mt-2'><i className={icon}></i> {Math.abs(diff)} ({decimal(ratio * 100)}%) </div>
}



function PastMonthCost({account}:{account:FullReportEntry}){
    const diff = account.totalCost - account.pastMonthSameDay.cost
    const icon  = diff > 0 ? 'fa fa-arrow-up' : 'fa fa-arrow-down';
    const ratio = account.totalCost ? diff / account.totalCost : 100

    return <div className='mt-2'><i className={icon}></i> {formatMoney(Math.abs(diff))} ({decimal(ratio * 100)}%) </div>
}

export function AccountDetail(){
    const {user} = useContext(AuthContext);
    const {account, showPrevious} = useContext(AccountContext);
    const {leadAvg, costAvg, pastLeadAvg, pastCostAvg, missingInfoInDays} = useMemo(() =>{
        const leadAvg = avg(account.dailyReports, a => a.leads);
        const costAvg = avg(account.dailyReports, a => a.cost);
        const pastLeadAvg = account.past ? avg(account.past.dailyReports, a => a.leads) : 0;
        const pastCostAvg = account.past ? avg(account.past.dailyReports, a => a.cost) : 0;
        const missingInfoInDays = missingInfoDayCount(account);

        return {leadAvg, costAvg, pastLeadAvg, pastCostAvg, missingInfoInDays};

    }, [account])

    useEffect(function(){
        //document.querySelectorAll('.account-list .account-item')

        function handleShortcuts(e:KeyboardEvent){
            var isDoc = e.target == document.body || e.target == document
            if(!isDoc) return;
            //if(!e.ctrlKey) return;

            if(e.key == 'ArrowRight' || e.code == "Space"){
                e.preventDefault();
                let all:Element[] = Array.from(document.querySelectorAll('.account-list .account-item'));
                let current = all.findIndex(a => a.classList.contains('selected'))
                if(current < all.length - 1){
                    fixOffset(all[current + 1] as any);
                    (all[current + 1] as any).click();
                }
            }

            if(e.key == 'ArrowLeft'){
                e.preventDefault();
                let all:Element[] = Array.from(document.querySelectorAll('.account-list .account-item'));
                let current = all.findIndex(a => a.classList.contains('selected'))
                if(current >  0){
                    fixOffset(all[current - 1] as any);
                    (all[current - 1] as any).click();
                }
            }


            //console.log(e);
        }

        window.addEventListener('keydown', handleShortcuts)
        return () => window.removeEventListener('keydown', handleShortcuts)

    }, [])

    useEffect(function(){
        logEvent({type: "account_view", params: getAccountEventInfo(account)})
    }, [account])



    return <>
        <h3>{account.name}</h3>

        <PeriodLabel />

        <div className='row mt-4'>
            <div className='d-lg-flex'>
                <div className='me-4'>
                    <b>{isGoogle(account) ? 'Google ID' : 'Meta ID'}: </b>{account.metaId}<br />
                    <b>Gestor: </b>{account.manager}<br />
                    <b>Grupo: </b>{account.group || "Não definido"}<br />
                    {account.consultant && <><b>Consultor: </b>{account.consultant}<br /></>}
                    <b>Estado: </b>{account.state}
                </div>
                <div className=''>
                    {account.pageId && <><b>Page ID: </b>{account.pageId}<br /></>}
                    <Balance account={account} />
                    {isGoogle(account) && <><b>Otimização: </b> {decimal((account.score || 0) * 100)}%<br /></>}
                    <b>Status: </b><StatusRender account={account} /> {account.slDate != null ? formatStrDate(account.slDate) : ''}<br />
                    {missingInfoInDays > 0 && <><b>Dias sem Informações: </b> {missingInfoInDays}<br /></>}
                    <b>Tendência: </b><TrendRender account={account} />
                </div>
            </div>
        </div>

        {/*<div className='mt-3 d-flex gap-2 align-items-start'>
            <b>Tags:</b><TagDisplay tags={[{color:'success', id:1, name:"VIP"}, {color:'info', id:2, name:"Personalizada"}, {color:'warning', id:3, name:"Preço R$ 95,00"}]} selected={[3, 2, 1]} />
        </div>

        <div className='mt-3 '>
            <TagPicker tags={[]}  selected={[]}/>
        </div>*/}
        <AccountTags account={account} />
        <div className='mt-3'><TagAccountEditorButton account={account} /></div>
       
        <ToggleFilter on={showPrevious} label="Mostrar Mês Passado" toggleAction="setShowPrevious" />

        <div className='counter-cards'>
            <CounterCard title='Leads' count={account.nLeads} footer={<PastMonthLeads account={account} />} />
            <MoneyCounterCard title='Investimento' count={account.totalCost} footer={<PastMonthCost account={account} />} />
        </div>
        
        <div className='row mt-4'>
            <div className='col-lg mt-3'>
                <h4>Leads</h4>
                <LeadChart account={account} />
            </div>
            <div className='col-lg mt-3'>
                <h4>Cliques</h4>
                <ClickChart account={account} />
            </div>
            <div className='col-lg mt-3'>
                <h4>Investimento</h4>
                <CostChart account={account} />
            </div>
        </div>

        <div className='account-info mt-4'>
            <h4>Informações deste mês</h4>
            <div className={`info-list-panels ${showPrevious ? 'show-past-month' : ''}`}>
                <table className='info-panel'>
                    <tbody>
                        {isGoogle(account) && <tr><td>Otimização:</td><td> <PercentNumberRender current={account.score} prev={account.past?.score} /> </td></tr>}
                        <tr><td>Impressões:</td><td> <BigNumberRender current={account.impressions} prev={account.past?.impressions} /> </td></tr>
                        {!isGoogle(account) && <tr><td>Alcance:</td><td> <BigNumberRender current={account.reach} prev={account.past?.reach} /> </td></tr>}
                        <tr><td>Clicks:</td><td> <BigNumberRender current={account.clicks} prev={account.past?.clicks} /> </td></tr>
                        <tr><td>Leads:</td><td> <BigNumberRender current={account.nLeads} prev={account.past?.nLeads} /> </td></tr>
                        <tr><td>Projeçao de Leads:</td><td> <BigNumberRender current={getLeadsProjection(account?.nLeads ?? 0)} prev={account.past?.nLeads ?? 0} /> </td></tr>
                        <tr><td>Investimento:</td><td> <MoneyRender current={account.totalCost} prev={account.past?.totalCost} /> </td></tr>
                    </tbody>
                </table>  

                <table className='info-panel'>
                    <tbody>
                        <tr><td>CPL:</td><td> <MoneyRender current={cpl(account)} prev={cpl(account.past)} /> </td></tr>
                        <tr><td>CPM:</td><td> <MoneyRender current={cpm(account)} prev={cpm(account.past)} /></td></tr>
                        <tr><td>CPI:</td><td> <MoneyRender current={cpi(account)} prev={cpi(account.past)} /></td></tr>
                        <tr><td>Expectativa de Leads:</td><td> {bigNumber(account.expectedLeads)}</td></tr>
                        
                    </tbody>
                </table>  

                <table className='info-panel'>
                    <tbody>
                        <tr><td>Orçamento:</td><td> <MoneyRender current={account.budget} prev={account.past?.budget || 0} /> </td></tr>
                        <tr><td>Orçamento diário:</td><td> <MoneyRender current={account.dayBudget} prev={account.past?.dayBudget || 0} /> </td></tr>
                        <tr><td>Investimento diário:</td><td><MoneyRender current={costAvg} prev={pastCostAvg} /></td></tr>
                        <tr><td>Investimento necessário:</td><td><MoneyRender current={suggestedBudget(account)} prev={suggestedBudget(account.past)} /></td></tr>
                    </tbody>
                </table>  

                { !isGoogle(account) &&
                    <table className='info-panel'>
                        <tbody>
                            <tr><td><ReactTooltip tooltip="Meta geral do mês" delay={300} placement="top-start">Meta:</ReactTooltip></td><td> 
                                <div className='d-flex'>
                                    <div><BigNumberRender current={account.goal} prev={account.past?.goal} /> </div>
                                    <div className='ms-3'><PercentRender current={monthGoal(account)} prev={monthGoal(account.past)} /> </div>
                                </div>
                            </td></tr>
                            <tr><td><ReactTooltip tooltip="Razão entre o número de leads atual e (a meta diária vezes o número de dias corridos)." delay={300} placement="top-start">Meta Proporcional:</ReactTooltip></td><td><span className={`badge ${goalClass(account)}`}>{decimal(account.goalRatio * 100)}%</span></td></tr>
                            <tr><td>Meta diária:</td><td><DecimalRender current={account.dayGoal} prev={account.past?.dayGoal} /> </td></tr>
                            <tr><td>Média diária:</td><td><DecimalRender current={leadAvg} prev={pastLeadAvg} /></td></tr>
                        </tbody>
                    </table>  
                }      

                { account.info?.filteredCampaigns != null &&
                    <table className='info-panel'>
                        <tbody>
                            <tr><td><ReactTooltip tooltip="Gastos de Engajamento + Trafego" delay={300} placement="top-start">Engajamento + Tráfego:</ReactTooltip></td><td>
                                     <b><MoneyRender current={account.totalCost + account.info.filteredCampaigns.cost} prev={(account.past?.totalCost || 0) + (account.past?.info?.filteredCampaigns?.cost || 0)} /></b>
                            </td></tr>
                            <tr><td>Investimento Tráfego:</td><td>
                                <MoneyRender current={account.info.filteredCampaigns.cost} prev={(account.past?.info?.filteredCampaigns?.cost)} />
                            </td></tr>
                            <tr><td>Impressões Tráfego:</td><td>
                                <BigNumberRender current={account.info.filteredCampaigns.impressions} prev={(account.past?.info?.filteredCampaigns?.impressions)} />
                            </td></tr>
                            <tr><td>Clicks Tráfego:</td><td>
                                <BigNumberRender current={account.info.filteredCampaigns.clicks} prev={(account.past?.info?.filteredCampaigns?.clicks)} />
                            </td></tr>
                            
                        </tbody>
                    </table>  
                }                
            </div>
        </div>

        <AccountLinks account={account} />

        {(user.isAdmin || user.isManager) && <div className='mt-4'>
            <Observation key={account.id} account={account} />
        </div>}
    </>


}